import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, RefreshCw, ExternalLink, Loader2 } from "lucide-react";
import { useAuth, isPlatformAdmin } from "../contexts/AuthContext";
import { SubscriptionInvitesPanel } from "../components/SubscriptionInvitesPanel";
import { CancelSubscriptionDialog } from "../components/CancelSubscriptionDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active:             { fg: "#2E7D5B", bg: "#EAF6EF", label: "Active" },
  trialing:           { fg: "#0091D5", bg: "#EAF1FB", label: "Trial" },
  past_due:           { fg: "#EA6A47", bg: "#FEF5F2", label: "Past Due" },
  unpaid:             { fg: "#B4331A", bg: "#FEE2DA", label: "Unpaid" },
  paused:             { fg: "#5A6B75", bg: "#F1F3F5", label: "Paused" },
  incomplete:         { fg: "#EA6A47", bg: "#FEF5F2", label: "Incomplete" },
  incomplete_expired: { fg: "#5A6B75", bg: "#F1F3F5", label: "Expired" },
  canceled:           { fg: "#5A6B75", bg: "#F1F3F5", label: "Canceled" },
  not_found:          { fg: "#5A6B75", bg: "#F1F3F5", label: "Not in live Stripe" },
};

function StatusPill({ status, testId }) {
  const s = STATUS_STYLE[status] || { fg: "#5A6B75", bg: "#F1F3F5", label: status || "Unknown" };
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 text-[10px] tracking-[0.05em] font-semibold rounded-[3px]"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.fg}` }}
      data-testid={testId}
    >
      {s.label}
    </span>
  );
}

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const BILLING_STATUSES = new Set(["active", "trialing", "past_due"]);

function PaymentHistory({ rowId }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/subscriptions/${rowId}/payments`)
      .then((r) => setItems(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [rowId]);
  if (err) return <div className="text-[12px] text-coral py-1" data-testid={`payments-error-${rowId}`}>{err}</div>;
  if (!items) return <div className="text-[12px] text-slate py-1 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading payments from Stripe…</div>;
  if (!items.length) return <div className="text-[12px] text-slate italic py-1" data-testid={`payments-empty-${rowId}`}>No payments yet.</div>;
  return (
    <table className="w-full text-[12px]" data-testid={`payments-table-${rowId}`}>
      <thead>
        <tr className="text-slate text-left">
          <th className="py-1 font-semibold">Date</th>
          <th className="py-1 font-semibold">Invoice</th>
          <th className="py-1 font-semibold text-right">Amount</th>
          <th className="py-1 font-semibold">Status</th>
          <th className="py-1 font-semibold">Receipt</th>
        </tr>
      </thead>
      <tbody>
        {items.map((p) => (
          <tr key={p.invoice_id} data-testid={`payment-row-${p.invoice_id}`}>
            <td className="py-1">{fmtDate(p.date)}</td>
            <td className="py-1 font-mono">{p.number || "—"}</td>
            <td className="py-1 text-right tabular-nums">{fmtMoney(p.amount)}</td>
            <td className="py-1 capitalize">{p.status}</td>
            <td className="py-1">
              {p.receipt_url ? (
                <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="text-sky hover:text-navy inline-flex items-center gap-1" data-testid={`payment-receipt-${p.invoice_id}`}>
                  View <ExternalLink size={10} />
                </a>
              ) : "—"}
              {p.pdf_url && (
                <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" className="ml-3 text-sky hover:text-navy" data-testid={`payment-pdf-${p.invoice_id}`}>PDF</a>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function SubscriptionsScreen() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showActive, setShowActive] = useState(true);
  const [showCancelled, setShowCancelled] = useState(false);
  const [cancelRow, setCancelRow] = useState(null);
  const cols = 8;
  const shownRows = rows.filter((r) => (BILLING_STATUSES.has(r.status) ? showActive : showCancelled));
  const activeCount = rows.filter((r) => BILLING_STATUSES.has(r.status)).length;

  useEffect(() => {
    axios.get(`${API}/admin/subscriptions`)
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/admin/subscriptions/refresh`);
      setRows(r.data || []);
      toast.success("Subscriptions refreshed from Stripe");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="panel-compact flex flex-col" data-testid="screen-subscriptions">
      <div className="section-title-sm flex items-baseline justify-between" style={{ borderBottomColor: "#EA6A47" }}>
        <span style={{ fontSize: 20, fontWeight: 700 }} className="inline-flex items-center gap-2">
          <CreditCard size={18} className="text-navy" strokeWidth={1.8} />
          Subscriptions
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-[12px] text-navy" data-testid="subscriptions-filter">
            <span className="font-semibold">Show:</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="subscriptions-filter-active">
              <input type="checkbox" checked={showActive} onChange={(e) => setShowActive(e.target.checked)} className="accent-[#1C4E80]" />
              Active ({activeCount})
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="subscriptions-filter-cancelled">
              <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} className="accent-[#1C4E80]" />
              Cancelled ({rows.length - activeCount})
            </label>
          </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          data-testid="subscriptions-refresh-btn"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy border border-navy px-2.5 py-1 rounded-[3px] hover:bg-navy-wash transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh from Stripe
        </button>
        </div>
      </div>

      {err && <div className="mt-3 text-[12px] text-coral" data-testid="subscriptions-error">{err}</div>}

      {loading ? (
        <div className="mt-6 text-slate text-[13px] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-6 text-slate italic text-[13px]" data-testid="subscriptions-empty">No associations are billed through Stripe yet.</div>
      ) : (
        <table className="u-table u-table-dense mt-4" style={{ tableLayout: "auto", width: "100%" }} data-testid="subscriptions-table">
          <thead>
            <tr style={{ background: "#1C4E80", color: "#fff" }}>
              <th className="text-left" style={{ width: "22%" }}>Association</th>
              <th className="text-left">Monthly</th>
              <th className="text-left">Plan</th>
              <th className="text-left">Status</th>
              <th className="text-left">Next Renewal</th>
              <th className="text-left">Last Receipt</th>
              <th className="text-left">Stripe</th>
              <th className="text-left" />
            </tr>
          </thead>
          <tbody>
            {shownRows.length === 0 && (
              <tr><td colSpan={cols} className="text-center text-slate italic py-4" data-testid="subscriptions-filter-empty">No subscriptions match the selected filter.</td></tr>
            )}
            {shownRows.map((r) => (
              <React.Fragment key={r.row_id}>
              <tr data-testid={`subscription-row-${r.row_id}`}>
                <td className="text-navy font-semibold">
                  {r.name}
                  {r.firm_name && <span className="ml-2 text-[10.5px] text-slate font-normal">{r.firm_name}</span>}
                  {!r.active && <span className="ml-2 text-[10px] text-slate font-normal">(inactive)</span>}
                </td>
                <td className="tabular-nums font-semibold text-navy" style={{ textAlign: "left" }} data-testid={`subscription-monthly-${r.row_id}`}>
                  {r.monthly_amount != null ? fmtMoney(r.monthly_amount) : "—"}
                </td>
                <td data-testid={`subscription-plan-${r.row_id}`}>{r.plan || "—"}</td>
                <td>
                  <StatusPill status={r.status} testId={`subscription-status-${r.row_id}`} />
                  {r.refresh_error && (
                    <span className="ml-2 text-[11px] text-coral" data-testid={`subscription-refresh-error-${r.row_id}`}>
                      {r.refresh_error}
                    </span>
                  )}
                </td>
                <td data-testid={`subscription-renewal-${r.row_id}`}>{fmtDate(r.current_period_end)}</td>
                <td data-testid={`subscription-last-receipt-${r.row_id}`}>
                  {r.last_payment_at ? `${fmtDate(r.last_payment_at)} · ${fmtMoney(r.last_payment_amount)}` : "—"}
                  <button
                    type="button"
                    onClick={() => setOpenId(openId === r.row_id ? null : r.row_id)}
                    data-testid={`subscription-payments-toggle-${r.row_id}`}
                    className="ml-2 text-[12px] text-sky hover:text-navy transition-colors"
                  >
                    {openId === r.row_id ? "Hide payments ▾" : "Payments ›"}
                  </button>
                </td>
                <td>
                  {r.stripe_url ? (
                    <a
                      href={r.stripe_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`subscription-open-stripe-${r.row_id}`}
                      className="inline-flex items-center gap-1 text-[12px] text-sky hover:text-navy transition-colors"
                    >
                      Open in Stripe <ExternalLink size={11} />
                    </a>
                  ) : "—"}
                </td>
                <td>
                  {r.stripe_subscription_id && BILLING_STATUSES.has(r.status) && (
                    <button
                      type="button"
                      onClick={() => setCancelRow(r)}
                      data-testid={`subscription-cancel-btn-${r.row_id}`}
                      className="text-[12px] font-semibold text-coral border border-coral px-2 py-0.5 rounded-[3px] hover:bg-[#FEF5F2] transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
              {openId === r.row_id && (
                <tr data-testid={`subscription-payments-row-${r.row_id}`}>
                  <td colSpan={cols} style={{ background: "#F2F9FD" }}>
                    <PaymentHistory rowId={r.row_id} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #1C4E80" }} data-testid="subscriptions-total-row">
              <td className="font-bold text-navy">Total Monthly Income <span className="text-[10.5px] text-slate font-normal">(active, trial &amp; past-due)</span></td>
              <td className="font-bold text-navy tabular-nums" style={{ textAlign: "left" }} data-testid="subscriptions-monthly-total">
                {fmtMoney(rows.reduce((t, r) => t + (BILLING_STATUSES.has(r.status) ? Number(r.monthly_amount || 0) : 0), 0))}
              </td>
              <td colSpan={cols - 2} />
            </tr>
          </tfoot>
        </table>
      )}
      <CancelSubscriptionDialog
        row={cancelRow}
        onClose={() => setCancelRow(null)}
        onCancelled={(upd) => {
          setRows((prev) => prev.map((x) => (x.row_id === upd.row_id ? upd : x)));
          refresh();
        }}
      />
      {isPlatformAdmin(user) && <SubscriptionInvitesPanel />}
    </div>
  );
}
